import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, PageShell, LoadingState } from "@/components/app/page";
import { adminAPI } from "@/services/api";
import { getErrorMessage } from "@/utils/errorHelpers";
import { Building2, Mail, Phone, ReceiptText, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FORM = {
  company_display_name: "",
  legal_name: "",
  brand_baseline: "",
  order_receipt_phone: "",
  support_phone: "",
  whatsapp_phone: "",
  official_email: "",
  support_email: "",
  headquarters_address: "",
  payment_beneficiary_name: "",
  payment_instructions: "",
  order_receipt_contact_label: "",
  mc_to_usd_rate: "1",
  usd_to_cdf_rate: "2800",
  legal_footer: "",
  notes: "",
};

export default function PlatformSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_FORM);
  const [pendingChange, setPendingChange] = useState(null);

  const load = async () => {
    try {
      const res = await adminAPI.getPlatformSettings();
      const activeSettings = res.data?.settings || {};
      setSettings({ ...DEFAULT_FORM, ...activeSettings, notes: "" });
      setPendingChange(res.data?.pending_change || null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Chargement des informations ARTICO-MMJ impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await adminAPI.proposePlatformSettings(settings);
      toast.success("Modification proposee. Validation FULL requise.");
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Proposition impossible"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Chargement des informations ARTICO-MMJ..." />;

  return (
    <PageShell data-testid="platform-settings-page">
      <PageHeader
        eyebrow="administration"
        title="Informations ARTICO-MMJ"
        description="Centralise les coordonnees officielles utilisees dans les bons de commande, emails, supports et documents institutionnels."
      />

      {pendingChange && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col gap-3 py-4 text-sm text-amber-900 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="font-semibold">Une modification est en attente de validation FULL.</p>
              <p>Proposee par {pendingChange.proposed_by_name || pendingChange.proposed_by}. Les nouvelles donnees ne seront appliquees qu'après approbation.</p>
            </div>
            <ShieldCheck size={22} className="shrink-0 text-amber-700" />
          </CardContent>
        </Card>
      )}

      <form onSubmit={submit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <Section title="Identite institutionnelle" icon={Building2}>
            <Field label="Nom affiche" value={settings.company_display_name} onChange={(value) => update("company_display_name", value)} />
            <Field label="Raison sociale" value={settings.legal_name} onChange={(value) => update("legal_name", value)} />
            <Field label="Baseline" value={settings.brand_baseline} onChange={(value) => update("brand_baseline", value)} />
            <TextField label="Adresse / siege" value={settings.headquarters_address} onChange={(value) => update("headquarters_address", value)} />
          </Section>

          <Section title="Contacts officiels" icon={Phone}>
            <Field label="Telephone du bon de commande" value={settings.order_receipt_phone} onChange={(value) => update("order_receipt_phone", value)} />
            <Field label="Telephone support" value={settings.support_phone} onChange={(value) => update("support_phone", value)} />
            <Field label="WhatsApp officiel" value={settings.whatsapp_phone} onChange={(value) => update("whatsapp_phone", value)} />
            <Field label="Libelle contact PDF" value={settings.order_receipt_contact_label} onChange={(value) => update("order_receipt_contact_label", value)} />
          </Section>

          <Section title="Emails" icon={Mail}>
            <Field type="email" label="Email officiel" value={settings.official_email} onChange={(value) => update("official_email", value)} />
            <Field type="email" label="Email support" value={settings.support_email} onChange={(value) => update("support_email", value)} />
          </Section>

          <Section title="Paiement et documents" icon={ReceiptText}>
            <Field label="bénéficiaire paiement" value={settings.payment_beneficiary_name} onChange={(value) => update("payment_beneficiary_name", value)} />
            <Field type="number" label="Valeur 1 MC en USD" value={settings.mc_to_usd_rate} onChange={(value) => update("mc_to_usd_rate", value)} />
            <Field type="number" label="Taux 1 USD en CDF" value={settings.usd_to_cdf_rate} onChange={(value) => update("usd_to_cdf_rate", value)} />
            <TextField autoGrow label="Instructions de paiement" value={settings.payment_instructions} onChange={(value) => update("payment_instructions", value)} />
            <TextField label="Pied de page legal PDF" value={settings.legal_footer} onChange={(value) => update("legal_footer", value)} />
          </Section>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-[#2A4B46]">Note de modification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TextField label="Motif ou commentaire pour le FULL" value={settings.notes} onChange={(value) => update("notes", value)} />
            <Button type="submit" disabled={saving || !!pendingChange} className="gap-2 bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
              <Save size={16} /> {saving ? "Envoi..." : "Proposer les modifications"}
            </Button>
            <p className="text-xs leading-5 text-[#5C625F]">
              Ces informations sont sensibles parce qu'elles apparaissent dans les documents officiels MMJ. La modification passe donc par une validation FULL differente du proposeur.
            </p>
          </CardContent>
        </Card>
      </form>
    </PageShell>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="border-[#E6E4E0] shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-[#2A4B46]">
          <Icon size={19} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value || ""} onChange={(event) => onChange(event.target.value)} className="mt-1" />
    </div>
  );
}

function TextField({ label, value, onChange, autoGrow = false }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!autoGrow || !textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [autoGrow, value]);

  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        ref={textareaRef}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        onInput={(event) => {
          if (!autoGrow) return;
          event.currentTarget.style.height = "auto";
          event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`;
        }}
        rows={3}
        className={`mt-1 ${autoGrow ? "resize-none overflow-hidden" : ""}`}
      />
    </div>
  );
}
